import { useInfiniteQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { AGENT_URL } from '@/config/env'
import { messageKeys } from '@/lib/queryKeys'
import { fetchJsonWithAuth, requireAuthToken } from '@/lib/api'
import type { MessageRecord } from '@/features/messages/types'

export interface MessagesQueryParams {
  limit?: number
  patient_name?: string
  phone?: string
  direction?: string
  created_after?: string
  created_before?: string
}

export interface MessagesResponse {
  data: MessageRecord[]
  pagination?: {
    limit: number
    has_more: boolean
    next_cursor: string | null
  } | null
}

export function useMessagesQuery(params: MessagesQueryParams = {}) {
  const { getToken, userId } = useAuth()
  const { organization } = useOrganization()

  return useInfiniteQuery({
    queryKey: messageKeys.list(organization?.id, params, userId),
    enabled: !!organization?.id,
    queryFn: async ({ pageParam, signal }) => {
      const token = await requireAuthToken(getToken)
      const search = new URLSearchParams()
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) search.set(key, String(value))
      }
      if (pageParam) search.set('cursor', pageParam)
      return fetchJsonWithAuth<MessagesResponse>({
        token,
        url: `${AGENT_URL}/data/messages/list?${search}`,
        signal,
        errorMessage: 'Failed to fetch messages',
      })
    },
    getNextPageParam: (lastPage) =>
      lastPage.pagination?.has_more
        ? (lastPage.pagination.next_cursor ?? undefined)
        : undefined,
    initialPageParam: undefined as string | undefined,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}
